import type { Background } from '@/api/types/background'
import type { BackgroundDropSlot } from '@/lib/backgrounds/types/background-drop-slot'
import type { BackgroundSlotMap } from '@/lib/backgrounds/types/background-slot-map'

/**
 * Matches legacy's `AddBackgroundCmd` (page-editor's `undo2.js` ~line 857-909): `double` and
 * `left`/`right` are mutually exclusive slots on a spread — placing a double-page background
 * clears any single-page ones and vice versa, since a spread can't sensibly have both a full-
 * spread background and a page-specific one at the same time.
 */
export function applyBackgroundPlacement<T>(
  current: BackgroundSlotMap<T>,
  slot: BackgroundDropSlot,
  placed: T
): BackgroundSlotMap<T> {
  if (slot === 'double') {
    return { double: placed }
  }

  return { ...current, double: undefined, [slot]: placed }
}

export function clearBackgroundPlacement<T>(
  current: BackgroundSlotMap<T>,
  slot: BackgroundDropSlot
): BackgroundSlotMap<T> {
  return { ...current, [slot]: undefined }
}

/**
 * Resolves the drop slot for a keyboard-activated (Enter/Space) background placement, which has
 * no cursor position to hit-test — per product direction, a spread-size background always targets
 * the whole spread, and a page-size one fills the left page first, then the right, only falling
 * back to overwriting a page once both are already filled. A cover/single-page spread (only one
 * side in `availableSides`) always resolves to that one side. Returns `null` only when neither
 * side is available at all (no page to place onto).
 */
export function resolveKeyboardBackgroundDropSlot(
  background: Pick<Background, 'doublePageFlag'>,
  current: BackgroundSlotMap<unknown>,
  availableSides: { left: boolean; right: boolean }
): BackgroundDropSlot | null {
  if (!availableSides.left && !availableSides.right) return null
  if (background.doublePageFlag) return 'double'

  if (availableSides.left && !current.left) return 'left'
  if (availableSides.right && !current.right) return 'right'
  if (availableSides.left) return 'left'

  return 'right'
}
